import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { authorisedOptions, selectorState, eligibilityState } from './createShiftServiceRules';   // UA-4-F8 / F9
import { EligibilityNote, LOCKED_ROW_CLASS } from './ScreeningBadge';   // BS3-005
import { addItem, validateItems, saveExtraItems, saveCatalogueItems } from './extraServiceItems';   // client#2
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { assignmentBlock, toastAssignmentBlock } from '@/lib/friendlyCodes';
import { Plus, Trash2, User, Home, Users, ExternalLink, Info } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { SHIFT_PERIODS, autoClassifyPeriod } from './ShiftPeriodBadge';
import { useAppDialog } from '@/hooks/useAppDialog';
import { useStaffAvailabilityForShift, AvailabilityStatusBadge, useWorkerEligibility } from './StaffAvailabilityHelpers';
import { accidentalConflicts } from './shiftSheetRules';   // UC-B-3: a new shift has no group — every overlapping booking is an accidental conflict
import { TrinitiDatePicker, TrinitiTimePicker } from '@/components/triniti-datetime';
import { useAuth } from '@/lib/auth';
import SearchSelect from '@/components/common/SearchSelect';
import LineItemSelect, { lineItemOption } from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import { participantLabel, PARTICIPANT_SEARCH_HINT, STAFF_SEARCH_HINT } from '@/components/common/pickerLabels';

const SHIFT_TYPES = [
  { value: 'individual', label: '1:1 Support', icon: User, hint: 'One participant, one or more services.' },
  { value: 'sil', label: 'SIL House', icon: Home, hint: 'Group home: one staff supporting multiple residents.' },
  { value: 'centre', label: 'Centre Session', icon: Users, hint: 'Group day program at a venue.' },
];

const fundingPlanLabel = (p) => {
  if (!p) return '—';
  const name = p.plan_name || p.name || p.plan_number;
  if (name) return name;
  if (p.id) return `Plan #${String(p.id).slice(-6)}`;
  return '—';
};

const newActivityRow = (order, start, end) => ({
  _key: Math.random().toString(36).slice(2),
  participant_id: '',
  service_catalog_id: '',
  funding_plan_id: '',
  allocation_start_time: start || '09:00',
  allocation_end_time: end || '17:00',
  allocation_order: order,
});

export function CalendarCreateShiftModal({ open, onClose, onCreated, prefillDate, prefillStaffId, prefillParticipantId, participants, services, staff }) {
  const { dangerConfirm } = useAppDialog();
  const navigate = useNavigate();
  const [shiftType, setShiftType] = useState('individual');
  const [form, setForm] = useState({
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    participant_id: '',           // for 1:1
    service_id: '',
    participant_service_item_id: '',   // UA-4-F8
    agreement_line_id: '',
    branch_catalogue_item_id: '',   // SIL residents without a service agreement
    staff_id: '',
    shift_period: 'AUTO',
    notes: '',
    // SIL
    house_id: '',
    support_ratio: '1:3',
    linked_participants: [],
    staff_id_2: '',               // SIL 2:1 — second required staff
  });
  const [creating, setCreating] = useState(false);

  // Reset when re-opened
  useEffect(() => {
    if (open) {
      setShiftType('individual');
      setForm({
        date: prefillDate || format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00', end_time: '17:00',
        participant_id: prefillParticipantId || '', service_id: '', participant_service_item_id: '', agreement_line_id: '', branch_catalogue_item_id: '',
        staff_id: prefillStaffId || '',
        shift_period: 'AUTO', notes: '',
        house_id: '', support_ratio: '1:3', linked_participants: [], staff_id_2: '',
      });
      setActivities([]);
      setServiceItems([]);
      setHouses([]);
      setHouseResidents([]);
    }
  }, [open, prefillDate, prefillStaffId, prefillParticipantId]);

  // Multi-service activity rows (Option B)
  const [activities, setActivities] = useState([]);
  // client#2: extra service items (agreement lines) at creation. Empty = one item for the whole shift (default).
  const [serviceItems, setServiceItems] = useState([]);   // UA-4-F8: legacy 'Service Activities (Phase 3F)' block RETIRED (not rendered; Plan tab is the single segment editor)
  const [validationError, setValidationError] = useState('');
  const addActivity = useCallback(() => {
    setActivities(prev => {
      const row = newActivityRow(prev.length + 1, form.start_time, form.end_time);
      // In 1:1 mode, immediately bind the new row to the chosen participant
      // so the Funding-plan and Service dropdowns are usable from the first click.
      if (shiftType === 'individual' && form.participant_id) {
        row.participant_id = form.participant_id;
      }
      return [...prev, row];
    });
  }, [form.start_time, form.end_time, shiftType, form.participant_id]);
  const updateActivity = (key, patch) => setActivities(prev => prev.map(a => a._key === key ? { ...a, ...patch } : a));
  const removeActivity = (key) => setActivities(prev => prev.filter(a => a._key !== key));

  // SIL houses + residents (Option A + C)
  const [houses, setHouses] = useState([]);
  const [houseResidents, setHouseResidents] = useState([]);
  useEffect(() => {
    if (shiftType !== 'sil' || !open) return;
    (async () => {
      try {
        const res = await api.get('/roster/sil/houses');
        setHouses(res.data?.data || res.data?.houses || []);
      } catch (e) { /* non-blocking */ }
    })();
  }, [shiftType, open]);
  useEffect(() => {
    if (shiftType !== 'sil' || !form.house_id) { setHouseResidents([]); return; }
    (async () => {
      try {
        const res = await api.get(`/roster/sil/houses/${form.house_id}`);
        const residents = res.data?.data?.residents || res.data?.residents || [];
        setHouseResidents(residents);
        // auto-select all active residents by default
        const ids = residents.filter(r => r.status !== 'archived').map(r => r.participant_id || r.id).filter(Boolean);
        setForm(f => ({ ...f, linked_participants: ids }));
      } catch (e) { setHouseResidents([]); }
    })();
  }, [shiftType, form.house_id]);

  // Cache funding plans per participant so activity rows can resolve them
  const [planCache, setPlanCache] = useState({}); // {participant_id: [{id, plan_name, ...}]}
  const ensurePlans = useCallback(async (pid) => {
    if (!pid || planCache[pid]) return;
    try {
      const res = await api.get(`/funding/plans/by-participant/${pid}`);
      const plans = res.data?.data || res.data?.items || [];
      setPlanCache(prev => ({ ...prev, [pid]: plans }));
    } catch { setPlanCache(prev => ({ ...prev, [pid]: [] })); }
  }, [planCache]);

  // Cache per-participant funded service items (NDIS billing safety — only show services this participant is funded for).
  // Shape: { [participant_id]: [{ service_catalog_id, service_name, service_code, service_category, funding_plan_id, rate_per_unit }] }
  const [psiCache, setPsiCache] = useState({});
  const ensurePsi = useCallback(async (pid) => {
    if (!pid || psiCache[pid]) return;
    try {
      const res = await api.get(`/services/participant-items?participant_id=${pid}`);
      setPsiCache(prev => ({ ...prev, [pid]: res.data?.data || [] }));
    } catch { setPsiCache(prev => ({ ...prev, [pid]: [] })); }
  }, [psiCache]);

  // UA-4-F8: the "Show all" catalogue escape hatch for the primary service was retired (§14/§49).
  const [showAllActivity, setShowAllActivity] = useState({}); // {activityKey: bool}

  // Compute the eligible services for a participant + (optional) funding plan.
  // 1. Filter to PSI rows for this participant
  // 2. If funding plan picked, further restrict to PSI rows on that plan
  // 3. Map back to service_catalog entries (so we keep id/name/code).
  // 4. Returns null when no participant context (caller falls back to full catalog).
  const fundedServices = useCallback((participantId, fundingPlanId) => {
    if (!participantId) return null;
    const items = psiCache[participantId];
    if (!Array.isArray(items)) return null;
    let rows = items;
    if (fundingPlanId) rows = rows.filter(r => r.funding_plan_id === fundingPlanId);
    const seen = new Set();
    const result = [];
    for (const r of rows) {
      if (seen.has(r.service_catalog_id)) continue;
      seen.add(r.service_catalog_id);
      const svc = services.find(s => s.id === r.service_catalog_id);
      if (svc) result.push(svc);
    }
    return result;
  }, [psiCache, services]);

  // When 1:1 participant selected, pre-fetch plans + PSI
  useEffect(() => { if (form.participant_id) { ensurePlans(form.participant_id); ensurePsi(form.participant_id); } }, [form.participant_id, ensurePlans, ensurePsi]);

  // UA-4-F8 (§8/§14/§49): Primary Service = S2 authorised-services for participant + date (ACTIVE agreement → lines → PSIs).
  // undefined = loading, null = failed/no participant, object = payload. Keyed by participant|date.
  const [authCache, setAuthCache] = useState({});
  const authKey = (pid, d) => `${pid}|${d}`;
  const ensureAuthorised = useCallback(async (pid, d) => {
    if (!pid || !d) return;
    const k = authKey(pid, d);
    if (k in authCache) return;
    setAuthCache(prev => ({ ...prev, [k]: undefined }));
    try {
      const res = await api.get(`/shifts/segments/authorised-services?participant_id=${pid}&service_date=${d}`);
      setAuthCache(prev => ({ ...prev, [k]: res.data?.data || res.data || null }));
    } catch { setAuthCache(prev => ({ ...prev, [k]: null })); }
  }, [authCache]);
  const ctxParticipants = shiftType === 'individual' ? [form.participant_id].filter(Boolean) : (form.linked_participants || []);
  useEffect(() => { ctxParticipants.forEach(pid => ensureAuthorised(pid, form.date)); }, [ctxParticipants.join(','), form.date, ensureAuthorised]); // eslint-disable-line react-hooks/exhaustive-deps
  const authorisedFor = (pid) => authCache[authKey(pid, form.date)];
  const primaryOptions = ctxParticipants.flatMap(pid => authorisedOptions(authorisedFor(pid), ctxParticipants.length > 1 ? (participants.find(p => p.id === pid)?.full_name || 'Resident') : null));
  const primaryState = ctxParticipants.length === 0
    ? { disabled: true, reason: shiftType === 'individual' ? 'Select a participant first' : 'Link at least one resident to choose an authorised service', canCreate: false }
    : (ctxParticipants.length === 1 ? selectorState(authorisedFor(ctxParticipants[0]), authorisedFor(ctxParticipants[0]) === undefined)
      : (primaryOptions.length ? { disabled: false, reason: '', canCreate: true, options: primaryOptions } : { disabled: true, reason: selectorState(authorisedFor(ctxParticipants[0]), authorisedFor(ctxParticipants[0]) === undefined).reason, canCreate: false }));
  // Client requirement (12-13 Oct 2026): Admin / Super Admin may roster a 1:1 or SIL shift when no participant on it has a
  // usable service agreement line (no ACTIVE agreement, or one without a Participant Schedule on the date), choosing the
  // line item from the service catalogue; it is billed from the participant's funding plan.
  const { user } = useAuth();
  const canRosterWithoutAgreement = ['Admin', 'Super Admin'].includes(user?.role_name);
  const ctxAuthLoaded = ctxParticipants.length > 0 && ctxParticipants.every(pid => authorisedFor(pid) !== undefined);
  const [useCatalogue, setUseCatalogue] = useState(false);   // admin opted out of the agreement lines that do exist
  useEffect(() => { setUseCatalogue(false); }, [open, shiftType, form.participant_id, (form.linked_participants || []).join(','), form.date]); // eslint-disable-line react-hooks/exhaustive-deps
  const noAgreementMode = shiftType === 'individual' && canRosterWithoutAgreement && ctxAuthLoaded && (primaryOptions.length === 0 || useCatalogue);
  const [naCtx, setNaCtx] = useState({});   // { [participant_id]: no-agreement-context payload }
  useEffect(() => {
    if (!noAgreementMode || !form.date) { setNaCtx({}); return; }
    let stale = false;
    Promise.all(ctxParticipants.map(pid => {
      const q = new URLSearchParams({ participant_id: pid, shift_date: form.date,
        ...(form.service_id ? { service_id: form.service_id } : {}), start_time: form.start_time || '', end_time: form.end_time || '' });
      return api.get(`/roster/no-agreement-context?${q}`).then(r => [pid, r.data?.data || null]).catch(() => [pid, null]);
    })).then(rows => { if (!stale) setNaCtx(Object.fromEntries(rows)); });
    return () => { stale = true; };
  }, [noAgreementMode, ctxParticipants.join(','), form.date, form.service_id, form.start_time, form.end_time]); // eslint-disable-line react-hooks/exhaustive-deps
  const participantName = (pid) => { const p = participants.find(pp => pp.id === pid); return p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.full_name : 'Resident'; };
  // SIL house shift where no ticked resident has a usable agreement line (or the admin chose the catalogue instead): the
  // line item comes from the house branch's Branch Catalogue, billed from each resident's funding plan (same Admin / Super Admin rule as 1:1).
  const silNoAgreementMode = shiftType === 'sil' && canRosterWithoutAgreement && !!form.house_id && ctxAuthLoaded && (primaryOptions.length === 0 || useCatalogue);
  const [silCtx, setSilCtx] = useState(null);
  useEffect(() => {
    if (!silNoAgreementMode || !form.date) { setSilCtx(null); return; }
    let stale = false;
    const q = new URLSearchParams({ house_id: form.house_id, shift_date: form.date, resident_ids: ctxParticipants.join(','),
      ...(form.branch_catalogue_item_id ? { branch_catalogue_item_id: form.branch_catalogue_item_id } : {}), start_time: form.start_time || '', end_time: form.end_time || '' });
    api.get(`/roster/no-agreement-sil-context?${q}`).then(r => { if (!stale) setSilCtx(r.data?.data || null); }).catch(() => { if (!stale) setSilCtx(null); });
    return () => { stale = true; };
  }, [silNoAgreementMode, form.house_id, form.date, ctxParticipants.join(','), form.branch_catalogue_item_id, form.start_time, form.end_time]); // eslint-disable-line react-hooks/exhaustive-deps
  const residentName = participantName;
  const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  // Stale validation (polish): clear the last error whenever an input changes.
  useEffect(() => { setValidationError(''); }, [form.participant_id, form.service_id, form.participant_service_item_id, form.date, form.start_time, form.end_time, (form.linked_participants || []).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  // When activity row participant_id changes, fetch plans + PSI for them
  useEffect(() => {
    activities.forEach(a => { if (a.participant_id) { ensurePlans(a.participant_id); ensurePsi(a.participant_id); } });
  }, [activities, ensurePlans, ensurePsi]);

  // Auto-bind activity participant_id to the 1:1 participant whenever it changes
  useEffect(() => {
    if (shiftType !== 'individual' || !form.participant_id) return;
    setActivities(prev => prev.map(a => a.participant_id === form.participant_id ? a : { ...a, participant_id: form.participant_id, funding_plan_id: a.participant_id === form.participant_id ? a.funding_plan_id : '' }));
  }, [shiftType, form.participant_id]);

  const autoPreview = form.shift_period === 'AUTO' ? autoClassifyPeriod(form.start_time, form.end_time) : null;

  // Live staff availability lookup
  const { statusMap, sortedStaff, enabled: availEnabled } = useStaffAvailabilityForShift(staff, {
    date: form.date,
    startTime: form.start_time,
    endTime: form.end_time,
  });
  const eligibility = useWorkerEligibility(staff, null, { date: form.date, participant_id: shiftType === 'individual' ? form.participant_id : null, linked_participants: shiftType === 'sil' ? (form.linked_participants || []) : [], start_time: form.start_time, end_time: form.end_time });   // UA-4-F9

  // Searchable + paged picker options (NEW-007). Availability badges, conflict and
  // eligibility reasons are kept as the row content; blocked rows stay disabled.
  const participantOptions = useMemo(() => (participants || []).map(p => ({
    value: p.id, label: participantLabel(p), keywords: p.ndis_number || '',
  })), [participants]);
  const staffRow = (s, { status, conflict, elig }) => (
    <span className="inline-flex items-center gap-2 w-full">
      <span className="truncate">{s.full_name}</span>
      {status && !elig?.disabled ? <AvailabilityStatusBadge status={status} /> : null}
      <EligibilityNote elig={elig} />
      {conflict ? <span className="text-[10px] text-red-700 truncate">{conflict.reason}</span> : null}
    </span>
  );
  const staffOptions = sortedStaff.map(s => {
    const key = s.user_id || s.id;
    const status = availEnabled ? statusMap[key]?.status : null;
    const conflict = accidentalConflicts(statusMap, [{ id: key, name: s.full_name }], [])[0];
    const elig = eligibilityState(key, eligibility);   // UA-4-F9
    return {
      value: key, label: s.full_name || '', keywords: `${s.email || ''} ${s.employee_id || ''}`,
      disabled: !!conflict || elig.disabled, title: conflict?.reason || elig.reason || undefined,
      className: elig.screening ? LOCKED_ROW_CLASS : undefined,   // BS3-005
      testid: elig.disabled ? `staff-option-ineligible-${key}` : (conflict ? `staff-option-conflict-${key}` : undefined),
      render: staffRow(s, { status, conflict, elig }),
    };
  });
  const staff2Options = sortedStaff
    .filter(s => (s.user_id || s.id) !== form.staff_id)
    .map(s => {
      const key = s.user_id || s.id;
      const status = availEnabled ? statusMap[key]?.status : null;
      const conflict = accidentalConflicts(statusMap, [{ id: key, name: s.full_name }], [])[0];
      const elig = eligibilityState(key, eligibility);   // BS3-005: the second 2:1 worker is checked like the first
      return {
        value: key, label: s.full_name || '', keywords: `${s.email || ''} ${s.employee_id || ''}`,
        disabled: !!conflict || elig.disabled, title: conflict?.reason || elig.reason || undefined,
        className: elig.screening ? LOCKED_ROW_CLASS : undefined,
        testid: elig.disabled ? `staff2-option-ineligible-${key}` : (conflict ? `staff-option-conflict-${key}` : undefined),
        render: staffRow(s, { status, conflict, elig }),
      };
    });

  // Effective participants list (1:1 = participants prop; SIL = selected residents only)
  const activityParticipants = useMemo(() => {
    if (shiftType === 'sil') {
      const idSet = new Set(form.linked_participants || []);
      return participants.filter(p => idSet.has(p.id));
    }
    if (shiftType === 'individual' && form.participant_id) {
      return participants.filter(p => p.id === form.participant_id);
    }
    return [];
  }, [shiftType, form.linked_participants, form.participant_id, participants]);

  const toggleResident = (pid) => {
    setForm(f => {
      const has = (f.linked_participants || []).includes(pid);
      return { ...f, linked_participants: has ? f.linked_participants.filter(x => x !== pid) : [...f.linked_participants, pid] };
    });
  };

  // client#2: only for an individual shift billed from agreement lines (SIL / no-agreement keep one line item)
  // Agreement lines (PSIs) — or, with no service agreement, catalogue line items billed from the funding plan.
  const catalogueItems = shiftType === 'individual' && noAgreementMode;
  const mainItemValue = catalogueItems ? form.service_id : form.participant_service_item_id;
  const multiItemsOn = shiftType === 'individual' && (catalogueItems ? !!form.service_id : (primaryState.canCreate && !!form.participant_service_item_id));
  const items = serviceItems.map((it, i) => (i === 0 ? { ...it, psi: mainItemValue } : it));
  const itemOptions = catalogueItems ? (services || []).map(s => lineItemOption(s, { showRate: false })) : (primaryState.options || []);
  const optionLabel = (v) => (itemOptions.find(o => o.value === v) || {}).label || '';
  const updateItem = (key, patch) => setServiceItems(prev => prev.map(it => (it._key === key ? { ...it, ...patch } : it)));
  const removeItem = (key) => setServiceItems(prev => { const next = prev.filter(it => it._key !== key); return next.length > 1 ? next : []; });
  const handleAddItem = () => {
    const base = items.length ? items : [{ _key: 'main', psi: mainItemValue, start: form.start_time, end: form.end_time }];
    setServiceItems(addItem(form, base));
  };
  // a new participant / date / shift type changes which lines are authorised → start again with one item
  useEffect(() => { setServiceItems([]); }, [form.participant_id, form.date, shiftType, noAgreementMode]);   // eslint-disable-line react-hooks/exhaustive-deps

  // client#2: the extra service item rows — shared by the agreement-line and the no-agreement (catalogue) blocks
  const extraItemsEditor = multiItemsOn ? (
    <div className="space-y-1.5 pt-1" data-testid="extra-service-items">
      {items.length > 1 && (
        <>
          <p className="text-[11px] text-muted-foreground">Each service item covers its own part of the shift ({form.start_time}–{form.end_time}).</p>
          {items.map((it, i) => (
            <div key={it._key} className="grid grid-cols-[1fr_auto_auto_auto] gap-1.5 items-center" data-testid={`service-item-row-${i}`}>
              {i === 0
                ? <span className="text-xs truncate" title={optionLabel(it.psi)}>Main: {optionLabel(it.psi) || '—'}</span>
                : (
                  <LineItemSelect options={itemOptions} value={it.psi} onChange={v => updateItem(it._key, { psi: v })}
                    placeholder={catalogueItems ? 'Select the line item' : 'Select the authorised service'} className="h-8 text-xs" testId={`service-item-${i}`} />
                )}
              <TrinitiTimePicker value={it.start} onChange={v => updateItem(it._key, { start: v })} testId={`service-item-start-${i}`} />
              <TrinitiTimePicker value={it.end} onChange={v => updateItem(it._key, { end: v })} testId={`service-item-end-${i}`} />
              {i === 0 ? <span className="w-7" /> : (
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeItem(it._key)} aria-label="Remove service item" data-testid={`service-item-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </>
      )}
      <button type="button" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1" onClick={handleAddItem} data-testid="add-service-item">
        <Plus className="h-3 w-3" /> Add service item
      </button>
    </div>
  ) : null;

  const validateBase = () => {
    if (!form.date || !form.start_time || !form.end_time) return 'Date, start, and end are required';
    if (noAgreementMode) {
      if (!form.service_id) return 'Select the line item for this shift';
    } else if (silNoAgreementMode) {
      if (!form.branch_catalogue_item_id) return 'Select the branch catalogue line item for this shift';
    } else {
      if (!primaryState.canCreate) return primaryState.reason || 'Select the authorised service';
      if (!form.participant_service_item_id) return 'Select the authorised service (agreement line) for this shift';
    }
    if (shiftType === 'individual' && !form.participant_id) return 'Select a participant';
    if (multiItemsOn && items.length > 1) { const e = validateItems(form, items); if (e) return e; }   // client#2
    if (shiftType === 'sil') {
      if (!form.house_id) return 'Select a SIL house';
      if (!(form.linked_participants || []).length) return 'Tick at least one resident';
      // 2:1 high-intensity SIL must have both staff assigned at creation time
      if ((form.support_ratio || '').trim() === '2:1') {
        const s1 = form.staff_id && form.staff_id !== 'none' ? form.staff_id : '';
        const s2 = form.staff_id_2 && form.staff_id_2 !== 'none' ? form.staff_id_2 : '';
        if (!s1 || !s2) return '2:1 high-intensity shifts require 2 staff to be assigned';
        if (s1 === s2) return '2:1 shift staff #1 and #2 must be different people';
      }
    }
    return null;
  };

  // Build the right payload + endpoint, then post activities
  const handleCreate = async () => {
    const err = validateBase();
    if (err) { setValidationError(err); toast.error(err); return; }
    setCreating(true);
    try {
      // Soft availability pre-check (only if assigning a single staff)
      if (form.staff_id && form.staff_id !== 'none') {
        try {
          const checkRes = await api.get(`/availability/check?user_id=${form.staff_id}&date=${form.date}&start_time=${form.start_time}&end_time=${form.end_time}`);
          const conflicts = checkRes.data?.data?.conflicts || [];
          if (conflicts.length > 0) {
            const labels = conflicts.map(c => c.label).join(', ');
            const ok = await dangerConfirm({
              title: 'Heads-up: availability conflict',
              description: `The selected staff has marked ${form.date} as ${labels}. Creating the shift with this assignment is allowed — please confirm.`,
              confirmLabel: 'Create anyway',
              cancelLabel: 'Pick someone else',
              variant: 'warning',
              testId: 'create-avail-conflict-confirm',
            });
            if (!ok) { setCreating(false); return; }
          }
        } catch { /* non-blocking */ }
      }

      let newShiftId = null;
      let extraItemErrors = [];

      if (shiftType === 'sil') {
        // SIL house shift creation
        // For 2:1 high-intensity, send BOTH required staff. For other ratios,
        // send the (optional) single staff if picked.
        const isTwoToOne = (form.support_ratio || '').trim() === '2:1';
        const silStaffIds = isTwoToOne
          ? [form.staff_id, form.staff_id_2].filter(s => s && s !== 'none')
          : (form.staff_id && form.staff_id !== 'none' ? [form.staff_id] : []);
        const payload = {
          house_id: form.house_id,
          service_id: silNoAgreementMode ? undefined : form.service_id,
          branch_catalogue_item_id: silNoAgreementMode ? form.branch_catalogue_item_id : undefined,
          roster_without_agreement: silNoAgreementMode || undefined,
          participant_service_item_id: form.participant_service_item_id || undefined,   // UA-4-F8
          agreement_line_id: form.agreement_line_id || undefined,
          shift_date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          assigned_staff_ids: silStaffIds,
          shift_type: form.shift_period === 'AUTO' ? 'SIL' : form.shift_period,
          notes: form.notes,
          support_ratio: form.support_ratio,
          support_type: 'sil',
          shift_context_type: 'accommodation',
          linked_house_id: form.house_id,
          linked_participants: form.linked_participants,
        };
        const res = await api.post('/roster/sil/create-shift', payload);
        newShiftId = res.data?.data?.id || res.data?.id || res.data?.data?.shift_id;
        const na = res.data?.no_agreement;
        [...(na?.warnings || []), ...(na?.residents || []).flatMap(r => (r.warnings || []).map(w => ({ ...w, message: `${residentName(r.participant_id)}: ${w.message}` })))]
          .forEach(w => toast.warning('Shift created without a service agreement', { description: w.message, duration: 10000 }));
        (res.data?.warnings || []).forEach(w => toast.warning(w.message, { duration: 8000 }));   // #15
      } else {
        // 1:1 individual shift
        const payload = {
          participant_id: form.participant_id,
          service_id: form.service_id,
          participant_service_item_id: form.participant_service_item_id || undefined,   // UA-4-F8
          agreement_line_id: form.agreement_line_id || undefined,
          shift_date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          shift_type: 'individual',
          shift_period: form.shift_period === 'AUTO' ? null : form.shift_period,
          notes: form.notes,
          required_staff: 1,
          roster_without_agreement: noAgreementMode || undefined,
        };
        const res = await api.post('/roster/shifts/create', payload);
        newShiftId = res.data?.data?.id || res.data?.id;
        (res.data?.no_agreement?.warnings || []).forEach(w => toast.warning('Shift created without a service agreement', { description: w.message, duration: 10000 }));
        (res.data?.warnings || []).forEach(w => toast.warning(w.message, { duration: 8000 }));   // #15
        if (multiItemsOn && items.length > 1 && newShiftId) {   // client#2: split the default segment + add the other items
          extraItemErrors = catalogueItems
            ? await saveCatalogueItems(api, newShiftId, form.participant_id, items)   // no agreement: every item becomes a catalogue segment
            : await saveExtraItems(api, newShiftId, form.participant_id, items, (v) => (primaryState.options || []).find(o => o.value === v));
        }
        if (form.staff_id && form.staff_id !== 'none' && newShiftId) {
          try { await api.post(`/roster/shifts/${newShiftId}/add-staff`, { staff_id: form.staff_id }); }
          catch (e) {
            const blk = assignmentBlock(e);
            toast.warning(blk ? `Shift created without the worker — ${blk.title}` : 'Shift created but the worker could not be assigned', { description: blk ? blk.reasons.join(' · ') || undefined : undefined });
          }
        }
      }

      // Persist activity rows (Option B + C)
      const activityErrors = [];
      if (false && newShiftId && activities.length) {   // UA-4-F8: retired — segments come from the authorised PSI at creation (default segment) and the Plan tab
        for (let i = 0; i < activities.length; i++) {
          const a = activities[i];
          if (!a.service_catalog_id || !a.participant_id || !a.funding_plan_id) {
            activityErrors.push(`Row ${i + 1}: incomplete`);
            continue;
          }
          try {
            await api.post(`/shifts/${newShiftId}/service-allocations`, {
              service_catalog_id: a.service_catalog_id,
              participant_id: a.participant_id,
              funding_plan_id: a.funding_plan_id,
              allocation_start_time: a.allocation_start_time,
              allocation_end_time: a.allocation_end_time,
              allocation_order: i + 1,
              is_primary: i === 0,
            });
          } catch (e) {
            activityErrors.push(`Row ${i + 1}: ${e?.response?.data?.detail?.message || e?.response?.data?.detail || 'failed'}`);
          }
        }
      }

      if (extraItemErrors.length) {
        toast.warning(`Shift created, but ${extraItemErrors.length} service item(s) could not be added — add them in the Plan tab`, { description: extraItemErrors.join(' · '), duration: 10000 });
      } else if (multiItemsOn && items.length > 1) {
        toast.success(`Shift created with ${items.length} service items`);
      } else if (activityErrors.length) {
        toast.warning(`Shift created but ${activityErrors.length} activity row(s) failed`, { description: activityErrors.join(' · ') });
      } else if (activities.length) {
        toast.success(`Shift created with ${activities.length} service activit${activities.length === 1 ? 'y' : 'ies'}`);
      } else {
        toast.success('Shift created');
      }
      onCreated();
      onClose();
    } catch (e) {
      const blk = assignmentBlock(e);
      if (blk) toastAssignmentBlock(toast, blk);
      else toast.error(e.response?.data?.error?.message || e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to create shift');
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-shift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> Create Shift</DialogTitle>
          <DialogDescription>Create a new shift from the scheduling board</DialogDescription>
        </DialogHeader>

        {/* Shift Type segmented control */}
        <div className="grid grid-cols-3 gap-2" data-testid="shift-type-segmented">
          {SHIFT_TYPES.map(t => {
            const Icon = t.icon;
            const active = shiftType === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setShiftType(t.value)}
                className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-2 text-xs transition ${active ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                data-testid={`shift-type-${t.value}`}
              >
                <Icon className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground -mt-2">{SHIFT_TYPES.find(t => t.value === shiftType)?.hint}</p>

        {/* Centre Session — link out (creating one is non-trivial, defer to dedicated page) */}
        {shiftType === 'centre' && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs space-y-2" data-testid="centre-cta">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-600 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">Centre sessions use a dedicated builder.</p>
                <p className="text-amber-700">Group day programs need venue, activity blocks, and per-participant attendance — too rich for a quick-create dialog.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" data-testid="centre-cta-open"
              onClick={() => { onClose(); navigate('/admin/centre-sessions'); }}>
              Open Centre Sessions Builder <ExternalLink className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}

        {/* Common date/time row */}
        {shiftType !== 'centre' && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <TrinitiDatePicker value={form.date} onChange={v => setForm({ ...form, date: v })} testId="shift-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Start</Label>
                <TrinitiTimePicker value={form.start_time} onChange={v => setForm({ ...form, start_time: v })} testId="shift-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End</Label>
                <TrinitiTimePicker value={form.end_time} onChange={v => setForm({ ...form, end_time: v })} testId="shift-end" />
              </div>
            </div>

            {/* 1:1 participant */}
            {shiftType === 'individual' && (
              <div className="space-y-1">
                <Label className="text-xs">Participant</Label>
                <SearchSelect
                  options={participantOptions}
                  value={form.participant_id}
                  onChange={v => setForm({ ...form, participant_id: v })}
                  placeholder="Select participant"
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  className="h-8 text-xs"
                  testId="shift-participant"
                />
              </div>
            )}

            {/* SIL house + residents (Option A + C) */}
            {shiftType === 'sil' && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">SIL House</Label>
                  <Select value={form.house_id} onValueChange={v => setForm({ ...form, house_id: v, branch_catalogue_item_id: '' })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="shift-sil-house"><SelectValue placeholder="Select SIL house" /></SelectTrigger>
                    <SelectContent>
                      {houses.length === 0 && (
                        <div className="px-2 py-2 text-xs text-muted-foreground">No SIL houses found. Create one under Accommodation → Properties.</div>
                      )}
                      {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name} {h.occupancy ? `(${h.occupancy}/${h.bed_count || '—'} beds)` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Support Ratio</Label>
                  <Select value={form.support_ratio} onValueChange={v => setForm({ ...form, support_ratio: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="shift-sil-ratio"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1:1">1:1 (one staff per resident)</SelectItem>
                      <SelectItem value="1:2">1:2</SelectItem>
                      <SelectItem value="1:3">1:3</SelectItem>
                      <SelectItem value="1:4">1:4</SelectItem>
                      <SelectItem value="2:1">2:1 (high-intensity, 2 staff per resident)</SelectItem>
                    </SelectContent>
                  </Select>
                  {(() => {   // #15: a 1:N shift takes at most N residents per worker
                    const m = /^1:(\d+)$/.exec((form.support_ratio || '').trim()); const n = m ? Number(m[1]) : 0;
                    const count = (form.linked_participants || []).length;
                    if (!n || n < 2 || !count) return null;
                    const workers = Math.ceil(count / n);
                    return <p className="text-[10px] text-muted-foreground" data-testid="shift-sil-ratio-hint">
                      {count} resident(s) at {form.support_ratio} need {workers} worker{workers > 1 ? 's' : ''} (max {n} per worker){count === 1 ? ' — consider 1:1 for a single resident' : ''}.
                    </p>;
                  })()}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center justify-between">
                    <span>Residents <span className="text-muted-foreground">({(form.linked_participants || []).length}/{houseResidents.length} selected)</span></span>
                  </Label>
                  {houseResidents.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic px-2 py-1.5 border rounded">Select a house first to see residents.</p>
                  ) : (
                    <div className="border rounded p-2 max-h-32 overflow-y-auto space-y-1" data-testid="shift-sil-residents">
                      {houseResidents.map(r => {
                        const pid = r.participant_id || r.id;
                        const p = participants.find(pp => pp.id === pid);
                        const label = (p ? `${p.first_name} ${p.last_name}`.trim() : '')
                          || r.name || r.participant_name || r.full_name || pid;
                        const checked = (form.linked_participants || []).includes(pid);
                        return (
                          <label key={pid} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                            <Checkbox checked={checked} onCheckedChange={() => toggleResident(pid)} data-testid={`shift-sil-resident-${pid}`} />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {noAgreementMode ? (
            <div className="space-y-1" data-testid="no-agreement-block">
              <Label className="text-xs">Line item <span className="text-muted-foreground">(no service agreement — billed from the funding plan)</span></Label>
              <LineItemSelect items={services || []} showRate={false} value={form.service_id} placeholder="Select the line item"
                onChange={v => setForm({ ...form, service_id: v, participant_service_item_id: '', agreement_line_id: '' })}
                className="h-8 text-xs" testId="shift-line-item" />
              {extraItemsEditor}
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-1" data-testid="no-agreement-panel">
                {useCatalogue && primaryOptions.length > 0 && (
                  <button type="button" className="text-primary hover:underline" data-testid="use-agreement-line"
                    onClick={() => { setUseCatalogue(false); setForm(f => ({ ...f, service_id: '' })); }}>
                    ← Use the agreement line instead
                  </button>
                )}
                <p className="font-medium">{useCatalogue && primaryOptions.length > 0 ? 'Line item chosen from the service catalogue instead of the agreement line.' : shiftType === 'sil'
                  ? `None of the selected residents has an active service agreement line for ${form.date}.`
                  : `This participant has no active service agreement line for ${form.date}.`}</p>
                {ctxParticipants.map(pid => {
                  const c = naCtx[pid];
                  if (!c) return null;
                  const who = shiftType === 'sil' ? <b>{participantName(pid)}: </b> : null;
                  return (
                    <div key={pid} className="space-y-0.5" data-testid={`no-agreement-ctx-${pid}`}>
                      {c.funding_plan ? (
                        <p data-testid="no-agreement-plan">{who}Funding plan <b>{c.funding_plan.name || 'Plan'}</b> ({c.funding_plan.start_date || '—'} – {c.funding_plan.end_date || '—'}) · remaining {money(c.funding_plan.remaining_budget)}
                          {c.funding_plan.category_remaining != null && <> · {c.funding_plan.category} remaining {money(c.funding_plan.category_remaining)}</>}
                          {form.service_id && c.estimated_cost > 0 && <> · this shift ~{money(c.estimated_cost)} ({c.hours}h × {money(c.rate)})</>}
                        </p>
                      ) : null}
                      {(c.warnings || []).map(w => <p key={w.code} className="text-amber-800" data-testid={`no-agreement-warning-${w.code}`}>⚠ {who}{w.message}</p>)}
                    </div>
                  );
                })}
              </div>
            </div>
            ) : silNoAgreementMode ? (
            <div className="space-y-1" data-testid="sil-no-agreement-block">
              <Label className="text-xs">Line item <span className="text-muted-foreground">(branch catalogue — billed from each resident's funding plan)</span></Label>
              <LineItemSelect value={form.branch_catalogue_item_id}
                options={(silCtx?.options || []).map(o => ({ value: o.id, label: `${o.name}${o.support_item_number ? ` · ${o.support_item_number}` : ''}${o.rate ? ` · ${money(o.rate)}/${o.unit || 'unit'}` : ''}`, keywords: o.support_item_number || '' }))}
                placeholder={silCtx && !(silCtx.options || []).length ? 'No branch catalogue items available' : 'Select the line item'}
                onChange={v => setForm({ ...form, branch_catalogue_item_id: v, service_id: '', participant_service_item_id: '', agreement_line_id: '' })}
                className="h-8 text-xs" testId="shift-branch-line-item" />
              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-1" data-testid="sil-no-agreement-panel">
                {useCatalogue && primaryOptions.length > 0 && (
                  <button type="button" className="text-primary hover:underline" data-testid="use-agreement-line"
                    onClick={() => { setUseCatalogue(false); setForm(f => ({ ...f, branch_catalogue_item_id: '' })); }}>
                    ← Use the agreement line instead
                  </button>
                )}
                <p className="font-medium">{useCatalogue && primaryOptions.length > 0
                  ? 'Line item chosen from the branch catalogue instead of the agreement line'
                  : `The selected residents have no active service agreement line for ${form.date}`} — each is billed from their own funding plan.</p>
                {form.branch_catalogue_item_id && silCtx?.estimated_cost_per_resident > 0 && <p>~{money(silCtx.estimated_cost_per_resident)} per resident ({silCtx.hours}h × {money(silCtx.rate)})</p>}
                {(silCtx?.residents || []).map(r => (
                  <p key={r.participant_id} data-testid={`sil-no-agreement-resident-${r.participant_id}`}>
                    <b>{residentName(r.participant_id)}</b>: {r.funding_plan ? <>plan {r.funding_plan.name || 'Plan'} · remaining {money(r.funding_plan.remaining_budget)}</> : 'no funding plan'}
                    {(r.warnings || []).map(w => <span key={w.code} className="block text-amber-800">⚠ {w.message}</span>)}
                  </p>
                ))}
                {(silCtx?.warnings || []).map(w => <p key={w.code} className="text-amber-800" data-testid={`sil-no-agreement-warning-${w.code}`}>⚠ {w.message}</p>)}
              </div>
            </div>
            ) : (
            <div className="space-y-1" data-testid="primary-service-block">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Authorised service <span className="text-muted-foreground">(ACTIVE agreement line → PSI, §14)</span></Label>
                {primaryState.canCreate && primaryOptions[0]?.agreement_no && <span className="text-[10px] text-muted-foreground" data-testid="primary-service-agreement">Agreement {primaryOptions[0].agreement_no}</span>}
              </div>
              <LineItemSelect options={primaryState.options || []} value={form.participant_service_item_id} disabled={primaryState.disabled}
                placeholder={primaryState.disabled ? primaryState.reason : 'Select the authorised service'}
                onChange={v => { const o = (primaryState.options || []).find(x => x.value === v); setForm({ ...form, participant_service_item_id: v, agreement_line_id: o?.agreement_line_id || '', service_id: o?.service_catalog_id || '' }); }}
                className="h-8 text-xs" testId="shift-service" triggerProps={{ title: primaryState.disabled ? primaryState.reason : undefined }} />
              {primaryState.disabled && <p className="text-[10px] text-amber-700" data-testid="primary-service-reason">{primaryState.reason}</p>}
              {extraItemsEditor}
              {canRosterWithoutAgreement && ctxAuthLoaded && primaryOptions.length > 0 && (
                <button type="button" className="text-[11px] text-primary hover:underline" data-testid="use-catalogue-line-item"
                  onClick={() => { setUseCatalogue(true); setForm(f => ({ ...f, participant_service_item_id: '', agreement_line_id: '', service_id: '', branch_catalogue_item_id: '' })); }}>
                  Use a catalogue line item instead (billed from the funding plan)
                </button>
              )}
            </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">
                {shiftType === 'sil' && (form.support_ratio || '').trim() === '2:1'
                  ? <>Assign Staff #1 <span className="text-red-600">(required for 2:1)</span></>
                  : <>Assign Staff <span className="text-muted-foreground">(optional)</span></>}
              </Label>
              <SearchSelect
                options={staffOptions}
                value={form.staff_id}
                onChange={v => setForm({ ...form, staff_id: v })}
                placeholder="No staff (open shift)"
                searchPlaceholder={STAFF_SEARCH_HINT}
                emptyText="No staff match that search"
                clearLabel="None (Open Shift)"
                clearValue="none"
                className="h-8 text-xs"
                testId="shift-staff"
              />
            </div>

            {/* 2:1 high-intensity SIL — second required staff */}
            {shiftType === 'sil' && (form.support_ratio || '').trim() === '2:1' && (
              <div className="space-y-1" data-testid="shift-staff-2-wrapper">
                <Label className="text-xs">
                  Assign Staff #2 <span className="text-red-600">(required for 2:1)</span>
                </Label>
                <SearchSelect
                  options={staff2Options}
                  value={form.staff_id_2}
                  onChange={v => setForm({ ...form, staff_id_2: v })}
                  placeholder="Select second staff"
                  searchPlaceholder={STAFF_SEARCH_HINT}
                  emptyText="No staff match that search"
                  className="h-8 text-xs"
                  testId="shift-staff-2"
                />
                <p className="text-[10px] text-amber-700">
                  2:1 high-intensity shifts must have both staff assigned at creation. They cannot be picked up by workers.
                </p>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Shift Period</Label>
              <Select value={form.shift_period} onValueChange={v => setForm({ ...form, shift_period: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="shift-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUTO">Auto-detect{autoPreview ? ` (${autoPreview})` : ''}</SelectItem>
                  {SHIFT_PERIODS.map(p => <SelectItem key={p} value={p}>{p.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Sleep-Over must be selected manually — it is never auto-classified.</p>
            </div>

            {/* Option B: multi-service activity rows */}
            {/* UA-4-F8: RETIRED (not deleted) — legacy Phase 3F multi-service activities; the Plan tab is the single segment editor. */}
            {false && (
            <div className="space-y-1 border-t pt-3" data-testid="shift-activities-section">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Service Activities <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                  onClick={addActivity}
                  disabled={activityParticipants.length === 0}
                  data-testid="shift-add-activity">
                  <Plus className="w-3 h-3 mr-1" /> Add activity
                </Button>
              </div>
              {activityParticipants.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">
                  {shiftType === 'individual' ? 'Pick a participant first to add activities.' : 'Select residents above to add activities for them.'}
                </p>
              ) : activities.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">
                  No activities added — shift will use the primary service only. Add activities to split into multiple invoice lines (Phase 3F multi-service billing).
                </p>
              ) : (
                <div className="space-y-2">
                  {activities.map((a, idx) => {
                    const plans = a.participant_id ? (planCache[a.participant_id] || []) : [];
                    return (
                      <div key={a._key} className="border rounded p-2 space-y-1.5 bg-slate-50" data-testid={`shift-activity-row-${idx}`}>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-[10px]">Activity {idx + 1}</Badge>
                          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600"
                            onClick={() => removeActivity(a._key)}
                            data-testid={`shift-activity-remove-${idx}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <TrinitiTimePicker value={a.allocation_start_time}
                            onChange={v => updateActivity(a._key, { allocation_start_time: v })}
                            testId={`shift-activity-start-${idx}`} />
                          <TrinitiTimePicker value={a.allocation_end_time}
                            onChange={v => updateActivity(a._key, { allocation_end_time: v })}
                            testId={`shift-activity-end-${idx}`} />
                        </div>
                        {shiftType === 'sil' && (
                          <Select value={a.participant_id}
                            onValueChange={v => updateActivity(a._key, { participant_id: v, funding_plan_id: '' })}>
                            <SelectTrigger className="h-7 text-xs" data-testid={`shift-activity-participant-${idx}`}>
                              <SelectValue placeholder="Participant for this activity" />
                            </SelectTrigger>
                            <SelectContent>
                              {activityParticipants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {shiftType === 'individual' && form.participant_id && !a.participant_id && null}
                        {(() => {
                          const funded = fundedServices(a.participant_id, a.funding_plan_id || null);
                          const showAll = !!showAllActivity[a._key];
                          const list = (showAll || !funded || funded.length === 0) ? services : funded;
                          const emptyFundedHint = funded && funded.length === 0 && a.participant_id;
                          return (
                            <>
                              <div className="flex items-center justify-between -mb-1">
                                <Label className="text-[10px] text-muted-foreground">Service</Label>
                                {funded && funded.length > 0 && (
                                  <button type="button"
                                    onClick={() => setShowAllActivity(prev => ({ ...prev, [a._key]: !prev[a._key] }))}
                                    className="text-[10px] text-primary hover:underline"
                                    data-testid={`shift-activity-service-toggle-${idx}`}>
                                    {showAll ? `Funded only (${funded.length})` : `Show all (${services.length})`}
                                  </button>
                                )}
                              </div>
                              <Select value={a.service_catalog_id}
                                onValueChange={v => updateActivity(a._key, { service_catalog_id: v })}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`shift-activity-service-${idx}`}>
                                  <SelectValue placeholder="Select service" />
                                </SelectTrigger>
                                <SelectContent>
                                  {emptyFundedHint && !showAll && (
                                    <div className="px-2 py-1.5 text-[10px] text-amber-700 bg-amber-50">
                                      No services funded{a.funding_plan_id ? ' under this plan' : ' for this participant'}.
                                    </div>
                                  )}
                                  {list.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.code}{s.code && s.name ? ` (${s.code})` : ''}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </>
                          );
                        })()}
                        <Select value={a.funding_plan_id}
                          onValueChange={v => updateActivity(a._key, { funding_plan_id: v })}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`shift-activity-funding-${idx}`}>
                            <SelectValue placeholder="Funding plan" />
                          </SelectTrigger>
                          <SelectContent>
                            {plans.length === 0 && (
                              <div className="px-2 py-1 text-[10px] text-muted-foreground">
                                {a.participant_id ? 'No funding plans for this participant.' : 'Pick a participant first.'}
                              </div>
                            )}
                            {plans.map(p => <SelectItem key={p.id} value={p.id}>{fundingPlanLabel(p)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="text-xs" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {shiftType !== 'centre' && (<>
            {validationError && <p className="text-[11px] text-red-700 mr-auto" data-testid="create-shift-validation">{validationError}</p>}
            <Button onClick={handleCreate} disabled={creating || (noAgreementMode ? !form.service_id : silNoAgreementMode ? !form.branch_catalogue_item_id : !primaryState.canCreate)} title={noAgreementMode ? (!form.service_id ? 'Select the line item' : undefined) : silNoAgreementMode ? (!form.branch_catalogue_item_id ? 'Select the line item' : undefined) : (!primaryState.canCreate ? primaryState.reason : undefined)} data-testid="create-shift-submit">
              {creating ? 'Creating...' : `Create ${SHIFT_TYPES.find(t => t.value === shiftType)?.label}`}
            </Button>
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
